import React, { useState, useRef, useEffect } from 'react';
import { DateTime } from 'luxon';
import { Table, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/auth.context';
import { getPromos } from '../../apis/atlantis.api';
import { PromoType } from '../../utils/constants';
import { formatDate } from '../../utils/helpers';
import { TierBadge, KindBadge } from './vipBadges';

// VIP tab, level 1 (TRU-876): every VIP promo — including ended ones, this is
// a history surface — most recent first. Clicking a row drills into its
// per-cycle winners (VipPromoHistory).
const VipPromos = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);

  useEffect(() => {
    authToken.current = auth?.idToken;
    fetchData();
  }, [auth?.idToken]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await getPromos(authToken.current);
      setPromos(
        (items ?? [])
          .filter(p => p.promoType === PromoType.VIP)
          .sort((a, b) => DateTime.fromISO(b.startTime) - DateTime.fromISO(a.startTime))
      );
    } catch (err) {
      console.error('Error fetching VIP promos:', err);
      setError('Failed to load VIP promos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center border-bottom">
        <div>
          <div className="game-micro">cycle history &amp; winners</div>
          <h4 className="mb-0">VIP</h4>
        </div>
      </div>
      {error && (
        <Alert variant="danger" className="my-3">
          {error}
        </Alert>
      )}
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table hover responsive className="my-3">
          <thead>
            <tr>
              <th>Promo</th>
              <th>Tier</th>
              <th>Kind</th>
              <th>Status</th>
              <th>Window</th>
            </tr>
          </thead>
          <tbody>
            {promos.map(promo => (
              <tr
                key={promo.promoId}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/vip/${promo.promoId}`)}
              >
                <td className="fw-semibold">{promo.promoName}</td>
                <td>{promo.vipTier ? <TierBadge tier={promo.vipTier} /> : '—'}</td>
                <td>
                  <KindBadge kind={promo.vipPromoKind} />
                </td>
                <td>{promo.status}</td>
                <td className="text-muted">
                  {formatDate(promo.startTime, DateTime.DATE_SHORT)} &ndash;{' '}
                  {formatDate(promo.endTime, DateTime.DATE_SHORT)}
                </td>
              </tr>
            ))}
            {!promos.length && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-4">
                  No VIP promos yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default VipPromos;
